import { createSlice, createAsyncThunk} from '@reduxjs/toolkit';
import {
  getUserCustomMetrics,
  getCustomMetricsSuggestions,
  addCustomMetric,
  updateCustomMetric,
  deleteCustomMetric,
  CustomMetric
} from '../../services/firebase/customMetrics.service';

// Types
export interface CustomMetricsState {
  customMetrics: Record<string, CustomMetric>;
  suggestedMetrics: Record<string, CustomMetric>;
  sortedCustomMetrics: string[];
  sortedSuggestedMetrics: string[];
  activeMetrics: string[];
  isLoading: boolean;
  error: string | null;
}

interface CustomMetricsData {
  customMetrics: Record<string, CustomMetric>;
  sortedCustomMetrics: string[];
  activeMetrics: string[];
}

interface SuggestedMetricsData {
  suggestedMetrics: Record<string, CustomMetric>;
  sortedSuggestedMetrics: string[];
}

// Initial state
const initialState: CustomMetricsState = {
  customMetrics: {},
  suggestedMetrics: {},
  sortedCustomMetrics: [],
  sortedSuggestedMetrics: [],
  activeMetrics: [],
  isLoading: false,
  error: null
};

// Async thunks
export const fetchUserCustomMetrics = createAsyncThunk<
  CustomMetricsData,
  string,
  { rejectValue: string }
>('customMetrics/fetchUserCustomMetrics', async (userId, { rejectWithValue }) => {
  try {
    const metricsData = await getUserCustomMetrics(userId);
    return metricsData;
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to fetch custom metrics');
  }
});

export const fetchSuggestedMetrics = createAsyncThunk<
  SuggestedMetricsData,
  void,
  { rejectValue: string }
>('customMetrics/fetchSuggestedMetrics', async (_, { rejectWithValue }) => {
  try {
    const suggestedMetrics = await getCustomMetricsSuggestions();
    return suggestedMetrics;
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to fetch suggested metrics');
  }
});

export const addUserCustomMetric = createAsyncThunk<
  { metricId: string; metric: CustomMetric },
  { userId: string; metric: CustomMetric },
  { rejectValue: string }
>('customMetrics/addUserCustomMetric', async ({ userId, metric }, { rejectWithValue }) => {
  try {
    const metricId = await addCustomMetric(userId, metric);
    return { metricId, metric };
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to add custom metric');
  }
});

export const updateUserCustomMetric = createAsyncThunk<
  { metricId: string; metric: Partial<CustomMetric> },
  { userId: string; metricId: string; metric: Partial<CustomMetric> },
  { rejectValue: string }
>('customMetrics/updateUserCustomMetric', async ({ userId, metricId, metric }, { rejectWithValue }) => {
  try {
    await updateCustomMetric(userId, metricId, metric);
    return { metricId, metric };
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to update custom metric');
  }
});

export const deleteUserCustomMetric = createAsyncThunk<
  string,
  { userId: string; metricId: string },
  { rejectValue: string }
>('customMetrics/deleteUserCustomMetric', async ({ userId, metricId }, { rejectWithValue }) => {
  try {
    await deleteCustomMetric(userId, metricId);
    return metricId;
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to delete custom metric');
  }
});

// Slice
const customMetricsSlice = createSlice({
  name: 'customMetrics',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch user's custom metrics
    builder.addCase(fetchUserCustomMetrics.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchUserCustomMetrics.fulfilled, (state, action) => {
      state.isLoading = false;
      state.customMetrics = action.payload.customMetrics;
      state.sortedCustomMetrics = action.payload.sortedCustomMetrics;
      state.activeMetrics = action.payload.activeMetrics;
    });
    builder.addCase(fetchUserCustomMetrics.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Failed to fetch custom metrics';
    });

    // Fetch suggested metrics
    builder.addCase(fetchSuggestedMetrics.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchSuggestedMetrics.fulfilled, (state, action) => {
      state.isLoading = false;
      state.suggestedMetrics = action.payload.suggestedMetrics;
      state.sortedSuggestedMetrics = action.payload.sortedSuggestedMetrics;
    });
    builder.addCase(fetchSuggestedMetrics.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Failed to fetch suggested metrics';
    });

    // Add custom metric
    builder.addCase(addUserCustomMetric.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(addUserCustomMetric.fulfilled, (state, action) => {
      state.isLoading = false;
      const { metricId, metric } = action.payload;
      state.customMetrics[metricId] = metric;
      state.sortedCustomMetrics.push(metricId);
      if (metric.isTracked) {
        state.activeMetrics.push(metricId);
      }
    });
    builder.addCase(addUserCustomMetric.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Failed to add custom metric';
    });

    // Update custom metric
    builder.addCase(updateUserCustomMetric.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(updateUserCustomMetric.fulfilled, (state, action) => {
      state.isLoading = false;
      const { metricId, metric } = action.payload;
      
      // Update the metric in state
      state.customMetrics[metricId] = {
        ...state.customMetrics[metricId],
        ...metric
      };
      
      // Update active metrics list if isTracked has changed
      if (metric.isTracked !== undefined) {
        if (metric.isTracked) {
          // Add to active metrics if not already there
          if (!state.activeMetrics.includes(metricId)) {
            state.activeMetrics.push(metricId);
          }
        } else {
          // Remove from active metrics
          state.activeMetrics = state.activeMetrics.filter(id => id !== metricId);
        }
      }
    });
    builder.addCase(updateUserCustomMetric.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Failed to update custom metric';
    });

    // Delete custom metric
    builder.addCase(deleteUserCustomMetric.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(deleteUserCustomMetric.fulfilled, (state, action) => {
      state.isLoading = false;
      const metricId = action.payload;
      
      // Remove from sortedCustomMetrics
      state.sortedCustomMetrics = state.sortedCustomMetrics.filter(id => id !== metricId);
      
      // Remove from activeMetrics if present
      state.activeMetrics = state.activeMetrics.filter(id => id !== metricId);
      
      // Remove from customMetrics
      delete state.customMetrics[metricId];
    });
    builder.addCase(deleteUserCustomMetric.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Failed to delete custom metric';
    });
  }
});

export const { clearError } = customMetricsSlice.actions;

export default customMetricsSlice.reducer;